/**
 * `skinStore` (W2-01b skins-and-builtin-effects; contract render/types.ts `SkinStore`; ARCHITECTURE.md §12,
 * skin-and-assets.md S§2-S§4, DESIGN.md §7.5 and D23).
 *
 *  - Before `init` (and until it resolves) `active` is the placeholder skin: the Phigros skin's SkinMetrics (S§3.6:
 *    texture sizes, holdAtlas [50, 50], holdAtlasMH [50, 95], hitFx 5x6, Phira's default colours) with one-pixel
 *    bitmaps in the note colours and no sounds.
 *  - `init({ storage, defaultId })` lists the skins and activates the default. Resolution order: the skin the user
 *    chose (`settings.preview.defaultSkin`, an installed respack or a built-in id) -> the local Phigros skin
 *    (dev/local builds only, served by the Vite plugin `phiedit-local-skin`) -> the procedural skin. A skin that
 *    fails to load is skipped (its reason is kept in `problems()`). `ready` resolves when init finished; it never
 *    rejects.
 *  - `list()`: the local Phigros skin (when present), the procedural skin, then the installed respacks.
 *  - `install(pack)` takes a pack that formats/respack already validated (the io worker unzips and validates;
 *    render never unzips), re-checks Phira's pack rules, decodes it (so a pack this browser cannot draw is refused
 *    before it is stored) and stores it. It does not activate it, except that installing the pack that is already
 *    active (same storage id) replaces the active copy with the freshly decoded one.
 *  - `setActive(id)` switches skins (the latest call wins); it rejects with a SkinError when the skin cannot be
 *    loaded, and the previous skin stays active. `remove(id)` deletes an installed respack and falls back to the
 *    resolution order when it was active. Subscribers hear every change of the active skin.
 *
 * Decoded skins are cached for the active skin and the procedural skin (the fallback); bitmaps of a skin that was
 * never handed out are closed when it is dropped.
 */

import type { MessageRef, Unsubscribe } from '../../core/util/types';
import { loadRespack } from '../../formats/respack/load';
import type { RespackFiles } from '../../formats/respack/types';
import type { LoadedSkin, SkinStorage, SkinStore, SkinSummary } from '../types';
import { checkPack, closeSkin, decodeSkin, errorRefs, SkinError, type SkinIdentity } from './decodeSkin';
import { fetchLocalSkinFiles, LOCAL_SKIN_ID } from './localSkin';
import {
  createProceduralSkin,
  placeholderImages,
  PROCEDURAL_METRICS,
  PROCEDURAL_SKIN_ID,
  PROCEDURAL_SKIN_INFO,
} from './procedural';
import { m } from './skin.messages';

export { LOCAL_SKIN_ID } from './localSkin';
export { PROCEDURAL_SKIN_ID } from './procedural';
export const PLACEHOLDER_SKIN_ID = 'placeholder';

/** Ids that never come from storage. */
export function isBuiltinSkinId(id: string): boolean {
  return id === PROCEDURAL_SKIN_ID || id === LOCAL_SKIN_ID || id === PLACEHOLDER_SKIN_ID;
}

/** What the store needs from the outside world (injectable for tests). */
export interface SkinStoreEnv {
  /** Draws the procedural skin (images + synthesised sounds). */
  procedural(): Promise<LoadedSkin>;
  /** Files of the local Phigros skin, or null when this build has none (throws when they cannot be fetched). */
  localFiles(): Promise<ReadonlyMap<string, Uint8Array> | null>;
  /** Decodes a validated pack. */
  decode(pack: RespackFiles, identity: SkinIdentity): Promise<LoadedSkin>;
}

/** This implementation always answers SkinStore.problems. */
export type SkinStoreWithProblems = SkinStore & Required<Pick<SkinStore, 'problems'>>;

const PROCEDURAL_SUMMARY: SkinSummary = {
  id: PROCEDURAL_SKIN_ID,
  name: PROCEDURAL_SKIN_INFO.name,
  author: PROCEDURAL_SKIN_INFO.author,
  source: 'procedural',
};

const MAX_PROBLEMS = 50;

let placeholder: LoadedSkin | null = null;

/** The placeholder skin (built lazily, on first read: its one-pixel images need a canvas). */
export function placeholderSkin(): LoadedSkin {
  placeholder ??= {
    id: PLACEHOLDER_SKIN_ID,
    name: PROCEDURAL_SKIN_INFO.name,
    source: 'procedural',
    metrics: PROCEDURAL_METRICS,
    images: placeholderImages(),
    holdBody: null,
    holdBodyMh: null,
    sounds: { click: null, drag: null, flick: null },
  };
  return placeholder;
}

function errorDetail(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function createSkinStore(envIn: Partial<SkinStoreEnv> = {}): SkinStoreWithProblems {
  const env: SkinStoreEnv = {
    procedural: envIn.procedural ?? (() => createProceduralSkin()),
    localFiles: envIn.localFiles ?? (() => fetchLocalSkinFiles()),
    decode: envIn.decode ?? ((pack, identity) => decodeSkin(pack, identity)),
  };

  let storage: SkinStorage | null = null;
  let builtins: SkinSummary[] = [];
  let installed: SkinSummary[] = [];
  let localPack: RespackFiles | null = null;
  let active: LoadedSkin | null = null;
  let initialised = false;
  let initRun: Promise<void> | null = null;
  let activation = 0;
  let proceduralJob: Promise<LoadedSkin> | null = null;
  const cache = new Map<string, LoadedSkin>();
  /** Skins that were active at some point: someone may still hold their bitmaps. */
  const handedOut = new WeakSet<LoadedSkin>();
  const listeners = new Set<(s: LoadedSkin) => void>();
  const problems: MessageRef[] = [];
  let resolveReady: () => void = () => undefined;
  const ready = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });

  const report = (...refs: MessageRef[]): void => {
    problems.push(...refs);
    if (problems.length > MAX_PROBLEMS) problems.splice(0, problems.length - MAX_PROBLEMS);
  };

  /** Drops a decoded skin nobody received. */
  const discard = (skin: LoadedSkin): void => {
    if (handedOut.has(skin) || cache.get(skin.id) === skin) return;
    closeSkin(skin);
  };

  const notify = (skin: LoadedSkin): void => {
    for (const cb of [...listeners]) {
      try {
        cb(skin);
      } catch (e) {
        // A failing subscriber must not stop the others nor the switch: report it like an uncaught error.
        if (typeof reportError === 'function') reportError(e);
        else console.error(e);
      }
    }
  };

  const activate = (skin: LoadedSkin): void => {
    if (active === skin) return;
    active = skin;
    handedOut.add(skin);
    cache.set(skin.id, skin);
    for (const [id, cached] of [...cache]) {
      if (id === PROCEDURAL_SKIN_ID || cached === skin) continue;
      cache.delete(id);
      if (!handedOut.has(cached)) closeSkin(cached);
    }
    notify(skin);
  };

  const procedural = (): Promise<LoadedSkin> => {
    if (proceduralJob === null) {
      const job = env.procedural().then((skin) => {
        cache.set(PROCEDURAL_SKIN_ID, skin);
        return skin;
      });
      proceduralJob = job;
      job.catch(() => {
        if (proceduralJob === job) proceduralJob = null;
      });
    }
    return proceduralJob;
  };

  const summaryOf = (id: string): SkinSummary | undefined =>
    builtins.find((s) => s.id === id) ?? installed.find((s) => s.id === id);

  /** Params naming a listed skin in a message: its display name (the id only when nothing is listed), plus the id. */
  const named = (id: string): { readonly id: string; readonly name: string } => ({
    id,
    name: summaryOf(id)?.name ?? id,
  });

  /** Why an unlisted skin cannot be used (no name is known, so the message does not name it). */
  const notListed = (id: string): MessageRef =>
    id === LOCAL_SKIN_ID ? { key: m.localMissing, params: { id } } : { key: m.unknownSkin, params: { id } };

  /** Loads (or takes from the cache) a listed skin. Throws a SkinError when it cannot be used. */
  const load = async (id: string): Promise<LoadedSkin> => {
    const cached = cache.get(id);
    if (cached !== undefined) return cached;
    if (id === PROCEDURAL_SKIN_ID) {
      try {
        return await procedural();
      } catch (e) {
        throw new SkinError(errorRefs(e, m.proceduralFailed));
      }
    }
    if (id === LOCAL_SKIN_ID) {
      if (localPack === null) throw new SkinError(notListed(id));
      try {
        return await env.decode(localPack, { id, source: 'localPhigros' });
      } catch (e) {
        throw new SkinError(errorRefs(e, m.localUnreachable));
      }
    }
    const summary = installed.find((s) => s.id === id);
    if (summary === undefined || storage === null) throw new SkinError(notListed(id));
    const skin = { id, name: summary.name };
    let pack: RespackFiles;
    try {
      pack = await storage.get(id);
    } catch (e) {
      throw new SkinError({ key: m.storedUnreadable, params: { ...skin, detail: errorDetail(e) } });
    }
    const invalid = checkPack(pack);
    if (invalid.length > 0) throw new SkinError([{ key: m.storedInvalid, params: skin }, ...invalid]);
    try {
      return await env.decode(pack, { id, source: 'respack' });
    } catch (e) {
      throw new SkinError([{ key: m.storedInvalid, params: skin }, ...errorRefs(e, m.storedUnreadable, skin)]);
    }
  };

  /**
   * Activates the first loadable skin of: preferred -> local Phigros -> procedural. A preferred skin that is not
   * listed (deleted pack, stale id, the local skin in a public build) is reported, so Settings > Skins can say why the
   * chosen skin is not the active one.
   */
  const resolveDefault = async (preferred: string | null): Promise<void> => {
    if (preferred !== null && summaryOf(preferred) === undefined) {
      report({ key: m.defaultMissing, params: { id: preferred } }, notListed(preferred));
    }
    const order: string[] = [];
    for (const id of [preferred, LOCAL_SKIN_ID, PROCEDURAL_SKIN_ID]) {
      if (id !== null && !order.includes(id) && summaryOf(id) !== undefined) order.push(id);
    }
    const seq = ++activation;
    for (const id of order) {
      try {
        const skin = await load(id);
        if (seq !== activation) {
          discard(skin);
          return;
        }
        activate(skin);
        return;
      } catch (e) {
        if (id === preferred) report({ key: m.defaultUnavailable, params: named(id) });
        report(...errorRefs(e, m.initFailed));
      }
    }
  };

  const runInit = async (deps: { readonly storage: SkinStorage; readonly defaultId: string | null }): Promise<void> => {
    storage = deps.storage;
    const nextBuiltins: SkinSummary[] = [PROCEDURAL_SUMMARY];
    localPack = null;
    try {
      const files = await env.localFiles();
      if (files !== null) {
        const { pack, validation } = loadRespack(files);
        if (pack !== null) {
          localPack = pack;
          nextBuiltins.unshift({
            id: LOCAL_SKIN_ID,
            name: pack.info.name,
            author: pack.info.author,
            source: 'localPhigros',
          });
        } else {
          report({ key: m.localInvalid, params: { count: validation.errors.length } }, ...validation.errors);
        }
      }
    } catch (e) {
      report(...errorRefs(e, m.localUnreachable));
    }
    builtins = nextBuiltins;
    try {
      const stored = await deps.storage.list();
      installed = stored
        .filter((s) => !isBuiltinSkinId(s.id))
        .map((s) => ({ id: s.id, name: s.name, author: s.author, source: 'respack' as const }));
    } catch (e) {
      installed = [];
      report({ key: m.storageListFailed, params: { detail: errorDetail(e) } });
    }
    // A cached skin that is no longer listed is dropped (re-init with another storage).
    for (const [id, skin] of [...cache]) {
      if (summaryOf(id) === undefined) {
        cache.delete(id);
        if (!handedOut.has(skin)) closeSkin(skin);
      }
    }
    await resolveDefault(deps.defaultId);
    initialised = true;
  };

  const store: SkinStoreWithProblems = {
    init(deps) {
      const previous = initRun ?? Promise.resolve();
      const run = previous
        .catch(() => undefined)
        .then(() => runInit(deps))
        .catch((e: unknown) => {
          report(...errorRefs(e, m.initFailed));
        })
        .finally(() => {
          resolveReady();
        });
      initRun = run;
      return run;
    },
    ready,
    list() {
      return initialised ? [...builtins, ...installed] : [];
    },
    get active() {
      return active ?? placeholderSkin();
    },
    async setActive(id) {
      if (initRun !== null) await initRun;
      const seq = ++activation;
      if (active?.id === id) return;
      const skin = await load(id);
      if (seq !== activation) {
        discard(skin);
        return;
      }
      activate(skin);
    },
    async install(pack) {
      if (initRun !== null) await initRun;
      const target = storage;
      if (target === null || !initialised) return { ok: false, errors: [{ key: m.notReady }] };
      const invalid = checkPack(pack);
      if (invalid.length > 0) return { ok: false, errors: invalid };
      let decoded: LoadedSkin;
      try {
        decoded = await env.decode(pack, { id: 'pending', source: 'respack' });
      } catch (e) {
        return { ok: false, errors: errorRefs(e, m.storageFailed) };
      }
      let id: string;
      try {
        id = await target.put(pack);
      } catch (e) {
        closeSkin(decoded);
        return { ok: false, errors: [{ key: m.storageFailed, params: { detail: errorDetail(e) } }] };
      }
      installed = [
        ...installed.filter((s) => s.id !== id),
        { id, name: pack.info.name, author: pack.info.author, source: 'respack' },
      ];
      const fresh: LoadedSkin = { ...decoded, id };
      const previous = cache.get(id);
      if (previous !== undefined && !handedOut.has(previous)) closeSkin(previous);
      if (active?.id === id) {
        // The pack in use was installed again: show exactly what is stored now (setActive would see the same id and
        // keep the old copy). A switch still in flight is not cancelled: install is not a switch.
        activate(fresh);
      } else {
        // Kept for the setActive that usually follows; dropped (and closed) at the next switch otherwise.
        cache.set(id, fresh);
      }
      return { ok: true, id };
    },
    async remove(id) {
      if (isBuiltinSkinId(id)) throw new SkinError({ key: m.builtinNotRemovable });
      if (initRun !== null) await initRun;
      if (storage === null) throw new SkinError({ key: m.notReady });
      await storage.remove(id);
      installed = installed.filter((s) => s.id !== id);
      const cached = cache.get(id);
      cache.delete(id);
      if (cached !== undefined && !handedOut.has(cached)) closeSkin(cached);
      if (active?.id === id) await resolveDefault(null);
    },
    subscribe(cb): Unsubscribe {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    problems() {
      return [...problems];
    },
  };
  return store;
}

export const skinStore: SkinStoreWithProblems = createSkinStore();
